import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FormProvider } from "react-hook-form";

import { PostShiftReviewHeader } from "./components/PostShiftReviewHeader";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { usePostShiftReviewNavigation } from "./hooks/usePostShiftReviewNavigation";
import { usePostShiftReviewSteps } from "./hooks/usePostShiftReviewSteps";
import { ChooseAnonymityStep } from "./steps/ChooseAnonymityStep";
import { CompletionStep } from "./steps/CompletionStep";
import { IntroStep } from "./steps/IntroStep";
import { ParkingQuestionStep } from "./steps/ParkingQuestionStep";
import { PeerReviewsStep } from "./steps/PeerReviewsStep";
import { QuestionStep } from "./steps/QuestionStep";
import { RateWorkplaceQuestionsStep } from "./steps/RateWorkplaceQuestionsStep";
import { RateWorkplaceStep } from "./steps/RateWorkplaceStep";
import { WriteReviewStep } from "./steps/WriteReviewStep";
import { type PostShiftReviewFlowContentProps, QuestionTag } from "./types";
import { usePostShiftReviewForm } from "./usePostShiftReviewForm";

export function PostShiftReviewFlowContent({
  modalState,
  facility,
  onComplete,
  shiftId,
  questionMappings,
  isLoading = false,
  isError = false,
  onSubmitRating,
  onSubmitReview,
  onSubmitReviewError,
  isDelayedReview = false,
  shift,
}: PostShiftReviewFlowContentProps) {
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const { form } = usePostShiftReviewForm({
    onSubmitRating,
    onSubmitReview,
  });

  const { stepTypes, getStepConfig } = usePostShiftReviewSteps({
    questionMappings,
    isError,
  });

  const { currentStep, handleStepAction, isSubmittingRatingLocally, isSubmittingReviewLocally } =
    usePostShiftReviewNavigation({
      stepTypes,
      form,
      onSubmitRating,
      onSubmitReview,
      onSubmitReviewError,
      onComplete,
      closeModal: modalState.closeModal,
      shiftId,
    });

  useLogEffect(
    APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_STEP_VIEWED,
    {
      step: currentStep,
      shiftId,
    },
    { enabled: modalState.modalIsOpen }
  );

  const loadingHeaderCurrentStep = isWriteReviewUiUpdateEnabled ? "intro" : "pick-rating";
  const loadingHeaderComputedSteps = isWriteReviewUiUpdateEnabled
    ? ([
        "intro",
        "peer-reviews",
        "rate-workplace-questions",
        "pick-rating",
        "write-review",
        "choose-anonymity",
        "end",
      ] as const)
    : (["pick-rating", "write-review", "end"] as const);

  const headerCurrentStep = isLoading ? loadingHeaderCurrentStep : currentStep;
  const headerComputedSteps = isLoading ? loadingHeaderComputedSteps : stepTypes;

  const currentStepConfig = getStepConfig(currentStep);

  return (
    <FormProvider {...form}>
      <FullScreenDialog
        modalState={modalState}
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.tertiary,
          }),
        }}
      >
        <PostShiftReviewHeader
          currentStep={headerCurrentStep}
          computedSteps={[...headerComputedSteps]}
          onBackOrClose={handleStepAction}
        />

        {isLoading ? (
          <DialogContent>
            <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
              <Text>Loading...</Text>
            </Stack>
          </DialogContent>
        ) : (
          <>
            {isError && (
              <Alert severity="error" sx={{ my: 2 }}>
                Unable to load review questions. Some steps may be unavailable.
              </Alert>
            )}

            <Box
              sx={(theme) => ({
                flex: 1,
                display: "flex",
                flexDirection: "column",
                mt: 5,
                p: 0,
                backgroundColor: theme.palette.background.tertiary,
              })}
            >
              {currentStep === "intro" && (
                <IntroStep
                  facilityName={facility.name}
                  facilityPhoto={facility.photo}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "peer-reviews" && (
                <PeerReviewsStep
                  workplaceId={facility.id}
                  workplaceName={facility.name}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "rate-workplace-questions" &&
                isDefined(currentStepConfig?.consolidatedQuestions) && (
                  <RateWorkplaceQuestionsStep
                    facilityName={facility.name}
                    questions={currentStepConfig.consolidatedQuestions}
                    isSubmitting={isSubmittingReviewLocally}
                    onAction={handleStepAction}
                  />
                )}
              {currentStep === "pick-rating" && (
                <RateWorkplaceStep
                  facilityName={facility.name}
                  facilityPhoto={facility.photo}
                  isSubmitting={isSubmittingRatingLocally}
                  isDelayedReview={isDelayedReview}
                  shift={shift}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "write-review" && (
                <WriteReviewStep
                  isSubmitting={isSubmittingReviewLocally}
                  isDelayedReview={isDelayedReview}
                  onAction={handleStepAction}
                />
              )}
              {(currentStep === QuestionTag.SOFTWARE ||
                currentStep === QuestionTag.SUPPLIES ||
                currentStep === QuestionTag.PATIENTS_PER_NURSE) &&
                isDefined(currentStepConfig) &&
                isDefined(currentStepConfig.questionType) && (
                  <QuestionStep
                    questionTag={currentStep}
                    questionText={currentStepConfig.questionText ?? ""}
                    questionType={currentStepConfig.questionType}
                    options={currentStepConfig.options}
                    numberConfig={currentStepConfig.numberConfig}
                    isSubmitting={isSubmittingReviewLocally}
                    onAction={handleStepAction}
                  />
                )}
              {currentStep === QuestionTag.PARKING && (
                <ParkingQuestionStep
                  isSubmitting={isSubmittingReviewLocally}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "choose-anonymity" && (
                <ChooseAnonymityStep
                  isSubmitting={isSubmittingReviewLocally}
                  onAction={handleStepAction}
                />
              )}
              {currentStep === "end" && <CompletionStep onAction={handleStepAction} />}
            </Box>
          </>
        )}
      </FullScreenDialog>
    </FormProvider>
  );
}
